import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCheckPermission } from '@/components/hooks/useCheckPermission';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import VadForm, { VadPresetError } from '@/components/molecules/admin/VadForm';
import { useEditVadPresetMutation, useVadPresetsQuery } from '@/types/graphql';

const VadEdit = () => {
  useCheckPermission({
    superAdmin: true,
  });

  const { t } = useTranslation('common');
  useSetHeaderTitle('VAD設定');

  const params = useParams();
  const snackbar = useSnackbar();
  const VadId = params.VadId;
  const navigate = useNavigate();
  const VadPresetResult = useVadPresetsQuery({
    variables: { input: { id: VadId } },
  });
  const [editVadPreset, editVadPresetResult] = useEditVadPresetMutation();
  const data = VadPresetResult.data?.vadPresets[0];

  return (
    <VadForm
      loading={editVadPresetResult.loading}
      readonly={data?.default}
      defaultData={useMemo(
        () => ({
          label: data?.label ?? '',
          smoothingTimeConstant: data?.smoothingTimeConstant ?? 0.95,
          energyOffset:
            data?.energyOffset != null
              ? Number(data.energyOffset).toLocaleString('en-US', {
                  maximumFractionDigits: 50,
                  useGrouping: false,
                })
              : '0.00000000001', // 1e-11
          energyThresholdRatioPos: data?.energyThresholdRatioPos ?? 2,
          energyThresholdRatioNeg: data?.energyThresholdRatioNeg ?? 0.5,
          energyIntegration: data?.energyIntegration ?? 0,
          vadThreshold: data?.vadThreshold ?? 0.5,
          vadVoicedThreshold: data?.vadVoicedThreshold ?? 0.3,
          vadUnvoicedThreshold: data?.vadUnvoicedThreshold ?? 0.5,
        }),
        [
          data?.label,
          data?.smoothingTimeConstant,
          data?.energyOffset,
          data?.energyThresholdRatioPos,
          data?.energyThresholdRatioNeg,
          data?.energyIntegration,
          data?.vadThreshold,
          data?.vadVoicedThreshold,
          data?.vadUnvoicedThreshold,
        ]
      )}
      onSubmit={async (input) => {
        const result = (
          await editVadPreset({
            variables: {
              input: {
                id: VadId ?? '',
                ...input,
                energyOffset: Number(input.energyOffset),
              },
            },
          })
        ).data?.editVadPreset;

        const serverValidationErrors: VadPresetError[] = [];
        if (result?.__typename === 'VadPresetNameAlreadyExists') {
          serverValidationErrors.push({
            type: 'VadPresetNameAlreadyExists',
            message: result.message,
          });
        } else {
          navigate('../');
          snackbar('success', t('message.successSaveChanges'));
        }

        return serverValidationErrors;
      }}
    />
  );
};
export default VadEdit;
